'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { createContext, useContext, useEffect, useState } from 'react';
import { LocalStorageKeys } from '~/config';

type RouteHistory = {
  current: string;
  previous: string | null;
};

const RouteHistoryContext = createContext<string | null>(null);

/**
 * Provides the route history context to its children, storing and updating the
 * current and previous routes in local storage.
 *
 * This provider component tracks the full path (pathname and search parameters)
 * of the current route and manages a previous route state, updating the local
 * storage accordingly. The context value provides access to the previous route
 * for any descendant component that consumes the context.
 */
export const RouteHistoryProvider = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const fullPath = `${pathname}${searchParams?.toString() ? `?${searchParams.toString()}` : ''}`;

  const [previousRoute, setPreviousRoute] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(LocalStorageKeys.ROUTE_HISTORY);
    let history: RouteHistory = { current: fullPath, previous: null };

    if (stored) {
      try {
        const parsed: RouteHistory = JSON.parse(stored);
        if (parsed.current.split('?')[0] !== fullPath.split('?')[0]) {
          history = {
            current: fullPath,
            previous: parsed.current,
          };
          setPreviousRoute(parsed.current);
        }
      } catch {
        // If bad data, fallback to safe defaults
        history = { current: fullPath, previous: null };
      }
    }

    localStorage.setItem(LocalStorageKeys.ROUTE_HISTORY, JSON.stringify(history));
  }, [fullPath]);

  return (
    <RouteHistoryContext.Provider value={previousRoute}>{children}</RouteHistoryContext.Provider>
  );
};

export const usePreviousRoute = () => useContext(RouteHistoryContext);
